import type { ProfileRow } from "@features/reports/api";
import { Card, Space, Statistic, theme } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type Props = {
  rows: ProfileRow[];
  dataMode: string;
  showLosses?: boolean;
};

type ChartPoint = {
  time: string;
  timestamp: string;
  value?: number | null;
  losses?: number | null;
  trend?: number | null;
  trendUpper?: number | null;
  trendLower?: number | null;
};

const formatNumber = (value?: number, digits = 3) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return value.toFixed(digits);
};

const ProfileChart = ({ rows, dataMode, showLosses = false }: Props) => {
  const { token } = theme.useToken();

  // Theme-aware colors
  const colors = useMemo(
    () => ({
      value: token.colorSuccess, // green
      losses: token.colorPrimary, // blue
      trend: token.colorWarning, // orange
      background: token.colorBgContainer,
      grid: token.colorBorderSecondary,
      text: token.colorText,
    }),
    [token],
  );

  const { chartData, hasTrend, max, last, sum, trendPerHour } = useMemo(() => {
    const points: ChartPoint[] = rows.map((row) => ({
      time: dayjs(row.period_start).format("DD.MM HH:mm"),
      timestamp: row.period_start,
      value: row.value,
      losses: row.value_with_losses,
      trend: row.trend_value,
      trendUpper: row.trend_upper_value,
      trendLower: row.trend_lower_value,
    }));

    const values = rows
      .map((r) => r.value)
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));

    const hasTrendData = rows.some((r) => r.trend_value != null);

    // Calculate trend slope (units per hour)
    let slope: number | undefined;
    if (hasTrendData && rows.length >= 2) {
      const first = rows.find((r) => r.trend_value != null);
      const last = [...rows].reverse().find((r) => r.trend_value != null);
      if (
        first &&
        last &&
        first !== last &&
        first.trend_value != null &&
        last.trend_value != null
      ) {
        const t1 = dayjs(first.period_start).valueOf();
        const t2 = dayjs(last.period_start).valueOf();
        const dt = (t2 - t1) / (1000 * 60 * 60); // hours
        if (dt > 0) {
          slope = (last.trend_value - first.trend_value) / dt;
        }
      }
    }

    return {
      chartData: points,
      hasTrend: hasTrendData,
      max: values.length > 0 ? Math.max(...values) : undefined,
      last: values.length > 0 ? values[values.length - 1] : undefined,
      sum: values.length > 0 ? values.reduce((a, b) => a + b, 0) : undefined,
      trendPerHour: slope,
    };
  }, [rows]);

  const label = dataMode === "power" ? "Мощность" : "Энергия";

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Card size="small" style={{ minWidth: 200 }}>
          <Statistic title={`Максимум (${label})`} value={max ?? 0} precision={3} />
        </Card>
        <Card size="small" style={{ minWidth: 200 }}>
          <Statistic title={`Последнее (${label})`} value={last ?? 0} precision={3} />
        </Card>
        <Card size="small" style={{ minWidth: 200 }}>
          <Statistic title={`Сумма (${label})`} value={sum ?? 0} precision={3} />
        </Card>
        <Card size="small" style={{ minWidth: 200 }}>
          <Statistic title="Точек" value={rows.length} />
        </Card>
        {hasTrend && trendPerHour !== undefined && (
          <Card size="small" style={{ minWidth: 200 }}>
            <Statistic title={`Тренд (${label}/час)`} value={trendPerHour} precision={4} />
          </Card>
        )}
      </Space>

      <div style={{ width: "100%", height: 420, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
            <XAxis dataKey="time" tick={{ fill: colors.text }} />
            <YAxis tick={{ fill: colors.text }} />
            <Tooltip
              formatter={(value: unknown) =>
                typeof value === "number" ? formatNumber(value) : "—"
              }
              contentStyle={{ backgroundColor: colors.background, borderColor: colors.grid }}
            />
            <Legend />
            {/* Trend corridor (±σ) */}
            {hasTrend && chartData.some((d) => d.trendUpper != null) && (
              <Area
                type="monotone"
                dataKey="trendUpper"
                stroke="none"
                fill={colors.trend}
                fillOpacity={0.15}
                name="Коридор тренда"
                isAnimationActive={false}
              />
            )}
            {hasTrend && chartData.some((d) => d.trendLower != null) && (
              <Area
                type="monotone"
                dataKey="trendLower"
                stroke="none"
                fill={colors.background}
                fillOpacity={1}
                legendType="none"
                isAnimationActive={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              name={label}
              stroke={colors.value}
              dot={false}
              isAnimationActive={false}
            />
            {showLosses && (
              <Line
                type="monotone"
                dataKey="losses"
                name={`${label} (потери)`}
                stroke={colors.losses}
                dot={false}
                isAnimationActive={false}
              />
            )}
            {hasTrend && (
              <Line
                type="monotone"
                dataKey="trend"
                name="Тренд"
                stroke={colors.trend}
                strokeDasharray="6 4"
                dot={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Space>
  );
};

export default ProfileChart;
